import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  TextField,
  Collapse,
  Autocomplete,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Delete,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ExpandMore,
  ExpandLess
} from "@mui/icons-material";
import { targetMuscles } from "../../../enums/TargetMuscles";
import { TempoTypes } from "../../../enums/TempoTypes";
import { getExercise, getByTypes } from "../../../services/ExcersiceService";
import { getProgramExercise, updateProgramExerciseRanges } from "../../../services/ExerciseHistoryService";
import { useMessageContext } from "../../../contexts/MessageContext";
import { getErrorMessage } from "../../../enums/ErrorMessages";
import { RTL } from "../../../cache/CacheRtl";
import { nonNegative, nonNegativeInputProps } from "../../../utils/numberInput";
import MobileIntervalSection from "./MobileIntervalSection";
import TraineeSwapNotice from "../TraineeSwapNotice";

const MobileExerciseCard = ({
  exercise,
  prvExercise,
  workout,
  setWorkout,
  editMode,
  openIntervals,
  setOpenIntervals,
  blockIdx,
  weekIdx,
  numberOfElements,
  traineeId
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedValues, setSelectedValues] = useState({
    targetMuscle: '',
    exerciseDef: null,
    exerciseOptions: []
  });
  const [exerciseRange, setExerciseRange] = useState({ minReps: null, maxReps: null, intervals: [] });

  const { notificationHandler } = useMessageContext();
  const exerciseKey = exercise.uuid || exercise.id || exercise.index;
  const isOpen = openIntervals?.[exerciseKey] || false;

  const setOpen = (value) => {
    setOpenIntervals(prev => ({
      ...prev,
      [exerciseKey]: typeof value === 'function' ? value(prev?.[exerciseKey] || false) : value
    }));
  };

  useEffect(() => {
    setLoading(true);
    if (exercise.exerciseId) {
      getExercise(exercise.exerciseId)
        .then(res => {
          getByTypes(res.data.targetMuscle)
            .then(resEx => {
              setSelectedValues({
                targetMuscle: res.data.targetMuscle,
                exerciseDef: res.data,
                exerciseOptions: resEx.data
              });
              setLoading(false);
            })
            .catch(e => {
              setLoading(false);
              notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            });
        })
        .catch(e => {
          setLoading(false);
          notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
        });

      // Fetch program exercise to get rep ranges
      if (traineeId) {
        getProgramExercise(traineeId, exercise.exerciseId)
          .then(res => {
            const programExercise = res.data;
            setExerciseRange({
              minReps: programExercise?.minReps ?? null,
              maxReps: programExercise?.maxReps ?? null,
              intervals: programExercise?.intervals || []
            });
          })
          .catch(() => {
            setExerciseRange({ minReps: null, maxReps: null, intervals: [] });
          });
      }
    } else {
      setSelectedValues({
        targetMuscle: '',
        exerciseDef: null,
        exerciseOptions: []
      });
      setExerciseRange({ minReps: null, maxReps: null, intervals: [] });
      setLoading(false);
    }
  }, [exercise, traineeId]);

  // Handler for updating interval range - updates local state and saves to server
  const handleIntervalRangeChange = (intervalIndex, field, value) => {
    if (!traineeId || !exercise.exerciseId) return;

    // Update local state immediately for responsive UI
    setExerciseRange(prev => {
      const newIntervals = [...(prev.intervals || [])];
      const existingIntervalIdx = newIntervals.findIndex(i => i.index === intervalIndex);

      if (existingIntervalIdx >= 0) {
        newIntervals[existingIntervalIdx] = {
          ...newIntervals[existingIntervalIdx],
          [field]: value
        };
      } else {
        newIntervals.push({
          index: intervalIndex,
          minReps: field === 'minReps' ? value : null,
          maxReps: field === 'maxReps' ? value : null
        });
      }

      const newRange = { ...prev, intervals: newIntervals };

      // Save to server
      updateProgramExerciseRanges(traineeId, exercise.exerciseId, {
        minReps: newRange.minReps,
        maxReps: newRange.maxReps,
        intervals: newIntervals
      }).catch(err => {
        console.error('Failed to save range:', err);
        notificationHandler.error('שגיאה בשמירת טווח החזרות');
      });

      return newRange;
    });
  };

  // Edit handlers
  const moveExerciseUp = () => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      const tmpId = programExercises.at(exercise.index - 1).index;
      programExercises.at(exercise.index - 1).index = programExercises.at(exercise.index).index;
      programExercises.at(exercise.index).index = tmpId;
      newWorkout.programExercises = programExercises;
      return newWorkout;
    });
  };

  const moveExerciseDown = () => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      const tmpId = programExercises.at(exercise.index + 1).index;
      programExercises.at(exercise.index + 1).index = programExercises.at(exercise.index).index;
      programExercises.at(exercise.index).index = tmpId;
      newWorkout.programExercises = programExercises;
      return newWorkout;
    });
  };

  const deleteExercise = () => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      let idx = 0;
      newWorkout.programExercises = newWorkout.programExercises
        .sort((a, b) => a.index > b.index ? 1 : -1)
        .filter(x => x.index !== exercise.index)
        .map(val => {
          val.index = idx;
          idx += 1;
          return val;
        });
      return newWorkout;
    });
  };

  const toggleFinished = () => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      programExercises.at(exercise.index).finished = !programExercises.at(exercise.index).finished;
      return newWorkout;
    });
  };

  const handleRestTime = (value) => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      programExercises.at(exercise.index).restTime = value;
      return newWorkout;
    });
  };

  const handleComment = (value) => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      programExercises.at(exercise.index).comment = value;
      return newWorkout;
    });
  };

  const updateTempo = (value) => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      programExercises.at(exercise.index).tempo = value;
      return newWorkout;
    });
  };

  const updateTargetMuscle = (value) => {
    setLoading(true);
    getByTypes(value)
      .then(res => {
        setSelectedValues(prv => ({
          ...prv,
          targetMuscle: value,
          exerciseDef: null,
          exerciseOptions: res.data
        }));
        setLoading(false);
      })
      .catch(e => {
        notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
        setLoading(false);
      });
  };

  const changeExercise = (value) => {
    const exerciseFound = selectedValues.exerciseOptions.find(op => op.id === value);
    setSelectedValues(prv => ({ ...prv, exerciseDef: exerciseFound }));
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      programExercises.at(exercise.index).exerciseId = exerciseFound.id;
      programExercises.at(exercise.index).comment = "";
      newWorkout.programExercises = programExercises;
      return newWorkout;
    });
  };

  const muscleName = targetMuscles.find(x => x.value === exercise.targetMuscle)?.name ||
                     targetMuscles.find(x => x.value === selectedValues.targetMuscle)?.name;
  const tempoName = TempoTypes.find(x => x.value === exercise.tempo)?.name;
  const isCardio = selectedValues.targetMuscle === 'CARDIO';

  if (loading) {
    return (
      <Paper elevation={0} sx={{
        backgroundColor: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        marginBottom: "12px",
        padding: "20px",
        textAlign: "center"
      }}>
        <Typography sx={{ color: "#94A3B8", fontSize: "13px" }}>טוען...</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: "#FFFFFF",
        border: exercise.finished ? "2px solid #22C55E" : "1px solid #E2E8F0",
        borderRadius: "12px",
        marginBottom: "12px",
        overflow: "hidden"
      }}
    >
      {/* Card Header */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 14px",
        backgroundColor: exercise.finished ? "#F0FDF4" : "#FCFCFD",
        borderBottom: "1px solid #E2E8F0"
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: "#64748B",
            backgroundColor: "#F1F5F9",
            borderRadius: "6px",
            padding: "4px 8px"
          }}>
            #{exercise.index + 1}
          </Typography>
          {muscleName && (
            <Chip
              label={muscleName}
              size="small"
              sx={{
                backgroundColor: "#EFF6FF",
                color: "#1D4ED8",
                fontSize: "11px",
                height: "24px"
              }}
            />
          )}
        </Box>
        {exercise.finished && (
          <CheckCircle sx={{ color: "#22C55E", fontSize: "20px" }} />
        )}
      </Box>

      {/* Exercise Name / Selection */}
      <Box sx={{ padding: "12px 14px" }}>
        {editMode ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Target Muscle Select */}
            <RTL>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedValues.targetMuscle || ""}
                  onChange={(e) => updateTargetMuscle(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: "13px" }}
                >
                  <MenuItem value="" disabled>בחר שריר</MenuItem>
                  {targetMuscles.map((res, i) => (
                    <MenuItem key={i} value={res.value}>{res.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </RTL>

            {/* Exercise Autocomplete */}
            {selectedValues.targetMuscle && (
              <RTL>
                <Autocomplete
                  disablePortal
                  fullWidth
                  value={selectedValues.exerciseDef}
                  getOptionLabel={(option) => option.nameHeb || ""}
                  disableClearable
                  onChange={(event, value) => changeExercise(value.id)}
                  options={selectedValues.exerciseOptions}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="תרגיל" size="small" />
                  )}
                />
              </RTL>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1E293B" }}>
              {selectedValues.exerciseDef?.nameHeb || "בחר תרגיל"}
            </Typography>
            {(exerciseRange.minReps || exerciseRange.maxReps) && (() => {
              // Check if all intervals have the same range (or no custom range)
              const hasVariableRange = exerciseRange.intervals?.some(interval =>
                (interval.minReps != null && interval.minReps !== exerciseRange.minReps) ||
                (interval.maxReps != null && interval.maxReps !== exerciseRange.maxReps)
              );
              return (
                <Typography sx={{
                  fontSize: "11px",
                  color: "#8B5CF6",
                  backgroundColor: "#F5F3FF",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontWeight: 500,
                  border: "1px solid #DDD6FE"
                }}>
                  {hasVariableRange ? 'טווח משתנה' : `טווח: ${exerciseRange.minReps || '?'}-${exerciseRange.maxReps || '?'}`}
                </Typography>
              );
            })()}
            <TraineeSwapNotice
              replacedByTrainee={exercise.replacedByTrainee}
              originalExerciseNameHeb={exercise.originalExerciseNameHeb}
              originalExerciseName={exercise.originalExerciseName}
              variant="inline"
              dense
            />
          </Box>
        )}
      </Box>

      {/* Quick Stats Row */}
      <Box sx={{
        display: "flex",
        padding: "0 14px 12px",
        gap: 1
      }}>
        {/* Sets */}
        {!isCardio && (
          <StatPill
            label="סטים"
            value={exercise.intervals?.length || 0}
            onClick={() => setOpen(!isOpen)}
          />
        )}

        {/* Rest Time */}
        <StatPill
          label="מנוחה"
          value={exercise.restTime}
          suffix="שניות"
          editable={editMode}
          onChange={handleRestTime}
          type="number"
        />

        {/* Tempo / Duration */}
        {isCardio ? (
          <StatPill
            label="משך"
            value={exercise.durationTime}
            suffix="דקות"
            editable={editMode}
            onChange={(val) => {
              setWorkout(prv => {
                const newWorkout = { ...prv };
                const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
                programExercises.at(exercise.index).durationTime = val;
                return newWorkout;
              });
            }}
            type="number"
          />
        ) : (
          editMode ? (
            <Box sx={{
              flex: 1,
              backgroundColor: "#F1F5F9",
              borderRadius: "8px",
              padding: "6px 8px"
            }}>
              <Typography sx={{ fontSize: "10px", color: "#94A3B8", mb: 0.5 }}>קצב</Typography>
              <RTL>
                <Select
                  value={exercise.tempo || ""}
                  onChange={(e) => updateTempo(e.target.value)}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={{ fontSize: "12px", "& .MuiSelect-select": { padding: "4px 8px" } }}
                >
                  {TempoTypes.map((res, i) => (
                    <MenuItem key={i} value={res.value} sx={{ fontSize: "12px" }}>{res.name}</MenuItem>
                  ))}
                </Select>
              </RTL>
            </Box>
          ) : (
            <StatPill label="קצב" value={tempoName || "-"} />
          )
        )}
      </Box>

      {/* Notes Section */}
      {(exercise.comment || editMode) && (
        <Box sx={{ padding: "0 14px 12px" }}>
          <Typography sx={{ fontSize: "10px", color: "#94A3B8", mb: 0.5 }}>הערות</Typography>
          {editMode ? (
            <TextField
              multiline
              fullWidth
              size="small"
              value={exercise.comment || ""}
              onChange={(e) => handleComment(e.target.value)}
              placeholder="הוסף הערה..."
              sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
            />
          ) : (
            <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
              {exercise.comment}
            </Typography>
          )}
        </Box>
      )}

      {/* Expand/Collapse Intervals */}
      {!isCardio && (
        <>
          <Box
            onClick={() => setOpen(!isOpen)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px",
              backgroundColor: "#F8FAFC",
              borderTop: "1px solid #E2E8F0",
              cursor: "pointer"
            }}
          >
            <Typography sx={{ fontSize: "12px", color: "#64748B", fontWeight: 500 }}>
              {isOpen ? "הסתר סטים" : "הצג סטים"}
            </Typography>
            {isOpen ? (
              <ExpandLess sx={{ fontSize: "18px", color: "#64748B" }} />
            ) : (
              <ExpandMore sx={{ fontSize: "18px", color: "#64748B" }} />
            )}
          </Box>

          <Collapse in={isOpen}>
            <MobileIntervalSection
              exercise={exercise}
              setWorkout={setWorkout}
              editMode={editMode}
              prvExercise={prvExercise}
              blockIdx={blockIdx}
              weekIdx={weekIdx}
              exerciseRange={exerciseRange}
              onRangeChange={handleIntervalRangeChange}
            />
          </Collapse>
        </>
      )}

      {/* Edit Mode Controls */}
      {editMode && (
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 14px",
          backgroundColor: "#FFFBEB",
          borderTop: "1px solid #FDE68A"
        }}>
          {/* Finished Toggle */}
          <IconButton onClick={toggleFinished} size="small">
            {exercise.finished ? (
              <CheckCircle sx={{ color: "#22C55E", fontSize: "22px" }} />
            ) : (
              <RadioButtonUnchecked sx={{ color: "#CBD5E1", fontSize: "22px" }} />
            )}
          </IconButton>

          {/* Move & Delete */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              onClick={moveExerciseUp}
              disabled={exercise.index === 0}
              size="small"
            >
              <KeyboardArrowUp sx={{ fontSize: "20px" }} />
            </IconButton>
            <IconButton
              onClick={moveExerciseDown}
              disabled={exercise.index === numberOfElements - 1}
              size="small"
            >
              <KeyboardArrowDown sx={{ fontSize: "20px" }} />
            </IconButton>
            <IconButton onClick={deleteExercise} size="small">
              <Delete sx={{ color: "#EF4444", fontSize: "20px" }} />
            </IconButton>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// Helper component for stat pills
const StatPill = ({ label, value, suffix, editable, onChange, type, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      backgroundColor: "#F1F5F9",
      borderRadius: "8px",
      padding: "6px 8px",
      textAlign: "center",
      cursor: onClick ? "pointer" : "default"
    }}
  >
    <Typography sx={{ fontSize: "10px", color: "#94A3B8", mb: 0.5 }}>{label}</Typography>
    {editable ? (
      <TextField
        value={value || ""}
        onChange={(e) => onChange(type === "number" ? nonNegative(e.target.value) : e.target.value)}
        type={type || "text"}
        size="small"
        variant="standard"
        inputProps={{
          ...(type === "number" ? nonNegativeInputProps : {}),
          style: { fontSize: "13px", textAlign: "center", padding: 0 }
        }}
        sx={{ "& .MuiInput-underline:before": { borderBottom: "none" } }}
      />
    ) : (
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B" }}>
        {value}{suffix ? ` ${suffix}` : ""}
      </Typography>
    )}
  </Box>
);

export default MobileExerciseCard;
